import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as XLSX from 'xlsx';
import { getAttendeeRegistrations } from "../../../../../../app/store/slices/event/registration/registration";
import { errorToast } from "../../../../../../utils/toasts/toasts";

export default function useHandleAttendeeSearch() {
    const dispatch = useDispatch();
    const { eventData } = useSelector((state) => state.event);
    const {
        totalRegistrations,
        totalTicketsSold,
        attendees
    } = useSelector((state) => state.attendeeRegistration);

    const [originalData, setOriginalData] = useState(attendees || []);
    const [filteredData, setFilteredData] = useState(attendees || []);
    const [searchQuery, setSearchQuery] = useState('');

    const [loading, setLoading] = useState(false);
    const [refreshLoading, setRefreshLoading] = useState(false)
    const [exportLoading, setExportLoading] = useState(false);
    const [error, setError] = useState(null);

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    function handleSearchFilter(query) {
        setSearchQuery(query);
        const lowerCaseQuery = query.toLowerCase();
        const filtered = originalData.filter((item) => {
            return (
                item?.attendee_name?.toLowerCase().includes(lowerCaseQuery) ||
                item?.email?.toLowerCase().includes(lowerCaseQuery) ||
                item?.phone?.includes(lowerCaseQuery) ||
                item?.ticket_type?.toLowerCase().includes(lowerCaseQuery) ||
                (item?.booking_number &&
                    item?.booking_number?.toLowerCase().includes(lowerCaseQuery)) ||
                (item?.ticket_number && item?.ticket_number.toString().toLowerCase().includes(lowerCaseQuery))
            );
        });
        setFilteredData(filtered);
    }


    async function onRefresh() {
        try {
            if (!eventData || !eventData.eventId) return;

            setRefreshLoading(true);

            const response = await dispatch(getAttendeeRegistrations(eventData?.eventId)).unwrap();

            if (response?.success && response?.code === 200) {
                const registrations = response?.data?.attendees || [];
                setOriginalData(registrations);
                setFilteredData(registrations);
            }
        } catch (error) {
            setError(error);
        } finally {
            setRefreshLoading(false);
        }
    }

    useEffect(() => {
        // Update data immediately when attendees changes
        if (attendees && attendees.length > 0) {
            setOriginalData(attendees);
            setFilteredData(attendees);
            setLoading(false);
        } else {
            // Only set loading to false if attendees is explicitly empty array
            if (attendees && attendees.length === 0) {
                setOriginalData([]);
                setFilteredData([]);
                setLoading(false);
            }
        }
    }, [attendees]);

    async function exportAttendeesList() {
        try {
            setExportLoading(true);

            const response = await dispatch(getAttendeeRegistrations(eventData?.eventId)).unwrap();

            if (response?.success && response?.code === 200) {
                const registrations = response?.data?.attendees || [];

                if (registrations.length === 0) {
                    errorToast('No attendee registrations found to export.');
                    return;
                }

                // Map the data to match the desired column names
                const mappedData = registrations.map((attendee) => ({
                    'Attendee Name': attendee.attendee_name || '',
                    'Email': attendee.email || '',
                    'Phone': attendee.phone || '',
                    'Ticket Type': attendee.ticket_type || '',
                    'Booking Number': attendee.booking_number || '',
                    'Ticket Number': attendee.ticket_number || '',
                    'Booked On': attendee.booked_on || ''
                }));

                const worksheet = XLSX.utils.json_to_sheet(mappedData);
                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workbook, worksheet, 'Attendees');
                XLSX.writeFile(workbook, `Attendee_Registrations_${Date.now()}.xlsx`);
            }
        } catch (error) {
            errorToast('An error occurred while exporting attendee registrations.');
        } finally {
            setExportLoading(false);
        }
    }


    return {
        originalData,
        filteredData,
        searchQuery,
        handleSearchFilter,
        loading,
        onRefresh,
        exportAttendeesList,
        exportLoading,
        error,
        refreshLoading,
        totalRegistrations,
        totalTicketsSold,
        setRowsPerPage,
        currentPage,
        setCurrentPage,
        rowsPerPage
    };
}